/**
 * GoogleAnalyticsService - Google Analytics integration
 */

import { createLogger } from '../Logger';

const log = createLogger('GoogleAnalyticsService');

declare global {
  interface Window {
    ga?: UniversalAnalytics.ga;
    gtag?: Gtag.Gtag;
    dataLayer?: unknown[];
  }
}

declare namespace UniversalAnalytics {
  interface ga {
    (...args: unknown[]): void;
    getAll?: () => Array<{ get: (name: string) => string }>;
  }
}

declare namespace Gtag {
  interface Gtag {
    (command: 'config', targetId: string, config?: Record<string, unknown>): void;
    (command: 'set', config: Record<string, unknown>): void;
    (command: 'event', eventName: string, eventParams?: Record<string, unknown>): void;
    (command: 'js', config: Date): void;
  }
}

class GoogleAnalyticsService {
  private initialized = false;
  private usingGtag = false;

  /**
   * Initialize Google Analytics
   */
  initialize(trackingId?: string): void {
    if (typeof window === 'undefined') return;
    
    // Check if gtag is available (GA4)
    if (window.gtag) {
      this.usingGtag = true;
      this.initialized = true;
      if (trackingId) {
        window.gtag('config', trackingId);
      }
      return;
    }
    
    // Check if ga is available (Universal Analytics)
    if (window.ga) {
      this.initialized = true;
      return;
    }
  }

  /**
   * Track page view
   * @param pagePath - Page path (e.g., '/dashboard/loans')
   * @param pageTitle - Page title (optional)
   */
  trackPageView(pagePath: string, pageTitle?: string): void {
    if (!this.initialized && typeof window !== 'undefined') {
      this.initialize();
    }
    
    if (!this.initialized) return;

    try {
      if (this.usingGtag && window.gtag) {
        window.gtag('event', 'page_view', {
          page_path: pagePath,
          page_title: pageTitle
        });
      } else if (window.ga) {
        window.ga('set', 'page', pagePath);
        window.ga('send', 'pageview');
      }
    } catch (error) {
      log.warn('Page view error', { error });
    }
  }

  /**
   * Track event
   * @param category - Event category
   * @param action - Event action
   * @param label - Event label (optional)
   * @param value - Event value (optional)
   */
  trackEvent(
    category: string,
    action: string,
    label?: string,
    value?: number
  ): void {
    if (!this.initialized && typeof window !== 'undefined') {
      this.initialize();
    }
    
    if (!this.initialized) return;

    try {
      if (this.usingGtag && window.gtag) {
        window.gtag('event', action, {
          event_category: category,
          event_label: label,
          value: value
        });
      } else if (window.ga) {
        window.ga('send', 'event', category, action, label, value);
      }
    } catch (error) {
      log.warn('Event error', { error });
    }
  }

  /**
   * Set user ID for tracking
   * @param userId - User ID
   */
  setUserId(userId: string): void {
    if (!this.initialized && typeof window !== 'undefined') {
      this.initialize();
    }
    
    if (!this.initialized) return;

    try {
      if (this.usingGtag && window.gtag) {
        window.gtag('set', { user_id: userId });
      } else if (window.ga) {
        window.ga('set', 'userId', userId);
      }
    } catch (error) {
      log.warn('setUserId error', { error });
    }
  }

  /**
   * Set custom dimension
   * @param index - Dimension index
   * @param value - Dimension value
   */
  setDimension(index: number, value: string): void {
    if (!this.initialized) return;

    try {
      if (window.ga) {
        window.ga('set', `dimension${index}`, value);
      }
    } catch (error) {
      log.warn('setDimension error', { error });
    }
  }

  /**
   * Track timing
   * @param category - Timing category
   * @param variable - Timing variable
   * @param value - Time in milliseconds
   * @param label - Timing label (optional)
   */
  trackTiming(
    category: string,
    variable: string,
    value: number,
    label?: string
  ): void {
    if (!this.initialized) return;

    try {
      if (this.usingGtag && window.gtag) {
        window.gtag('event', 'timing_complete', {
          name: variable,
          value: value,
          event_category: category,
          event_label: label
        });
      } else if (window.ga) {
        window.ga('send', 'timing', category, variable, value, label);
      }
    } catch (error) {
      log.warn('Timing error', { error });
    }
  }

  /**
   * Track exception/error
   * @param description - Error description
   * @param fatal - Whether the error is fatal
   */
  trackException(description: string, fatal: boolean = false): void {
    if (!this.initialized) return;

    try {
      if (this.usingGtag && window.gtag) {
        window.gtag('event', 'exception', {
          description: description,
          fatal: fatal
        });
      } else if (window.ga) {
        window.ga('send', 'exception', {
          exDescription: description,
          exFatal: fatal
        });
      }
    } catch (error) {
      log.warn('Exception tracking error', { error });
    }
  }

  /**
   * Track loan-related events
   * @param action - Loan action (view, pay, preclose, etc.)
   * @param data - Loan data
   */
  trackLoanEvent(
    action: string,
    data: {
      loanId: string;
      merchantName: string;
      amount?: number;
      status?: string;
    }
  ): void {
    this.trackEvent('Loans', action, data.loanId, data.amount);
  }

  /**
   * Track payment events
   * @param action - Payment action
   * @param data - Payment data
   */
  trackPaymentEvent(
    action: string,
    data: {
      paymentId?: string;
      amount: number;
      paymentType: string;
      status?: string;
    }
  ): void {
    this.trackEvent('Payment', action, data.paymentType, data.amount);
  }
}

// Export singleton instance
const googleAnalyticsService = new GoogleAnalyticsService();
export default googleAnalyticsService;

// Also export the class for testing
export { GoogleAnalyticsService };
